<app-header></app-header>
<app-leftsidebar></app-leftsidebar>
<main class="orders-shell">
<div class="orders-page">

  <!-- ===== Hero: title + stats ===== -->
  <header class="lib-hero">
    <div class="lib-hero-text">
      <span class="lib-eyebrow">Your collection</span>
      <h1>My Purchases</h1>
      <p class="lede">Everything you've bought or claimed — download it any time.</p>
    </div>
    @if (!loading && !loadFailed && orders.length) {
      <div class="lib-stats">
        <div class="lib-stat">
          <span class="lib-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
          </span>
          <div><strong>{{ library.length }}</strong><span>Item{{ library.length === 1 ? '' : 's' }}</span></div>
        </div>
        <div class="lib-stat">
          <span class="lib-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /></svg>
          </span>
          <div><strong>{{ fileCount }}</strong><span>File{{ fileCount === 1 ? '' : 's' }}</span></div>
        </div>
        <div class="lib-stat">
          <span class="lib-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
          </span>
          <div><strong>{{ totalSpent ? money(totalSpent) : '$0' }}</strong><span>Total spent</span></div>
        </div>
      </div>
    }
  </header>

  @if (loading) {
    <!-- ===== Loading skeleton ===== -->
    <div class="lib-toolbar lib-toolbar--skeleton" aria-hidden="true">
      <span class="lib-skel lib-skel--tabs"></span>
      <span class="lib-skel lib-skel--search"></span>
    </div>
    <div class="lib-grid" aria-busy="true" aria-label="Loading your purchases">
      @for (s of skeletons; track s) {
        <div class="lib-card lib-card--skeleton">
          <span class="lib-skel lib-skel--cover"></span>
          <div class="lib-card-body">
            <span class="lib-skel lib-skel--title"></span>
            <span class="lib-skel lib-skel--line"></span>
            <div class="lib-card-actions">
              <span class="lib-skel lib-skel--btn"></span>
              <span class="lib-skel lib-skel--icon"></span>
              <span class="lib-skel lib-skel--icon"></span>
            </div>
          </div>
        </div>
      }
    </div>
  } @else if (loadFailed) {
    <div class="lib-empty">
      <span class="lib-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      </span>
      @if (needsSignIn) {
        <h3>Please sign in again</h3>
        <p>Your sign-in has expired, so we couldn't load your purchases.</p>
        <a routerLink="/login" class="wt-btn-primary lib-empty-btn">Sign in</a>
      } @else {
        <h3>Couldn't load your purchases</h3>
        <p>Check your connection and try again.</p>
        <button type="button" class="wt-btn-primary lib-empty-btn" (click)="load()">
          <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
          Try again
        </button>
      }
    </div>
  } @else if (orders.length === 0) {
    <div class="lib-empty">
      <span class="lib-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
      </span>
      <h3>No purchases yet</h3>
      <p>Maps, modules and campaigns you buy or claim for free will show up here, ready to download.</p>
      <a routerLink="/store" class="wt-btn-primary lib-empty-btn">Browse the store</a>
    </div>
  } @else {
    <!-- ===== Tabs + search ===== -->
    <div class="lib-toolbar">
      <div class="lib-tabs" role="tablist" aria-label="View">
        <button type="button" role="tab" class="lib-tab" [class.is-active]="tab === 'library'" [attr.aria-selected]="tab === 'library'" (click)="tab = 'library'">
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
          Library <span class="lib-tab-count">{{ library.length }}</span>
        </button>
        <button type="button" role="tab" class="lib-tab" [class.is-active]="tab === 'orders'" [attr.aria-selected]="tab === 'orders'" (click)="tab = 'orders'">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2Z" /><line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
          Order history <span class="lib-tab-count">{{ orders.length }}</span>
        </button>
      </div>

      <label class="lib-search">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        <input
          type="search"
          [(ngModel)]="search"
          [placeholder]="tab === 'library' ? 'Search your library' : 'Search orders'"
          aria-label="Search your purchases"
        />
      </label>
    </div>

    @if (search.trim() && shownCount === 0) {
      <div class="lib-no-match">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        Nothing matches “{{ search }}”.
        <button type="button" class="lib-text-btn" (click)="search = ''">Clear search</button>
      </div>
    }

    @if (tab === 'library') {
      <!-- ===== Library grid ===== -->
      @if (library.length === 0 && !search.trim()) {
        <div class="lib-no-match">Your orders haven't finished paying yet — items appear here once payment clears.</div>
      }
      <div class="lib-grid">
        @for (item of filteredLibrary; track item.line.id) {
          <article class="lib-card" [class.is-open]="openFiles[item.line.id]">
            <a class="lib-card-cover" [routerLink]="['/marketplace/listing', item.line.listing]" tabindex="-1" aria-hidden="true">
              @if (item.line.listing_detail?.cover_image) {
                <img [src]="item.line.listing_detail.cover_image" alt="" loading="lazy" />
              } @else {
                <span class="lib-card-cover-placeholder">
                  <svg viewBox="0 0 24 24"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>
                </span>
              }
              @if (item.line.listing_detail?.category_detail) {
                <span class="lib-card-type">{{ item.line.listing_detail.category_detail?.name }}</span>
              }
              <span class="lib-card-owned">
                <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                Owned
              </span>
            </a>

            <div class="lib-card-body">
              <a class="lib-card-title" [routerLink]="['/marketplace/listing', item.line.listing]" [title]="item.line.listing_detail?.title">
                {{ item.line.listing_detail?.title || 'Listing #' + item.line.listing }}
              </a>
              <div class="lib-card-meta">
                @if (item.line.vendor_detail?.shop_name) {
                  <a [routerLink]="['/store']" [queryParams]="{ author: item.line.vendor_detail.shop_slug }">{{ item.line.vendor_detail.shop_name }}</a>
                  <span aria-hidden="true">·</span>
                }
                <span>{{ item.order.created_at | date: 'MMM d, y' }}</span>
              </div>

              <div class="lib-card-actions">
                @if (isDigital(item.line) && downloadsFor(item.line).length) {
                  @if (downloadsFor(item.line).length === 1) {
                    <button
                      type="button"
                      class="lib-dl-btn"
                      [disabled]="downloading[downloadKey(item.line, downloadsFor(item.line)[0])]"
                      (click)="download(item.line, downloadsFor(item.line)[0])"
                    >
                      @if (downloading[downloadKey(item.line, downloadsFor(item.line)[0])]) {
                        <span class="lib-spinner lib-spinner--dark" aria-hidden="true"></span> Preparing…
                      } @else {
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        Download
                      }
                    </button>
                  } @else {
                    <button
                      type="button"
                      class="lib-dl-btn"
                      [attr.aria-expanded]="!!openFiles[item.line.id]"
                      (click)="toggleFiles(item.line)"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                      Download
                      <span class="lib-dl-count">{{ downloadsFor(item.line).length }}</span>
                      <svg class="lib-dl-chevron" viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                    </button>
                  }
                } @else if (isDigital(item.line)) {
                  <span class="lib-card-note">Files coming soon</span>
                }

                @if (item.line.listing_detail?.can_review) {
                  <a class="lib-icon-btn lib-icon-btn--gold" [routerLink]="['/marketplace/listing', item.line.listing]" fragment="reviews" title="Write a review" aria-label="Write a review">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                  </a>
                }
                <a class="lib-icon-btn" [routerLink]="['/marketplace/listing', item.line.listing]" title="View item" aria-label="View item">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                </a>
              </div>

              @if (openFiles[item.line.id]) {
                <ul class="lib-files">
                  @for (file of downloadsFor(item.line); track file.key) {
                    <li>
                      <button
                        type="button"
                        class="lib-file"
                        [disabled]="downloading[downloadKey(item.line, file)]"
                        (click)="download(item.line, file)"
                        [attr.aria-label]="'Download ' + file.label"
                      >
                        <span class="lib-file-kind" [class]="'lib-file-kind lib-file-kind--' + fileKind(file)" aria-hidden="true">
                          @switch (fileKind(file)) {
                            @case ('image') {
                              <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                            }
                            @case ('doc') {
                              <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg>
                            }
                            @default {
                              <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /></svg>
                            }
                          }
                        </span>
                        <span class="lib-file-name" [title]="file.label">{{ file.label }}</span>
                        @if (downloading[downloadKey(item.line, file)]) {
                          <span class="lib-spinner" aria-hidden="true"></span>
                        } @else {
                          <svg class="lib-file-arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        }
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>
          </article>
        }
      </div>
    } @else {
      <!-- ===== Order history ===== -->
      <div class="lib-orders">
        @for (order of filteredOrders; track order.id) {
          <div class="lib-order">
            <span class="lib-order-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2Z" /><line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="12" y2="16" /></svg>
            </span>
            <div class="lib-order-main">
              <div class="lib-order-number">{{ order.order_number }}</div>
              <div class="lib-order-date">{{ order.created_at | date: 'MMM d, y · h:mm a' }}</div>
            </div>
            <div class="lib-order-items">
              <div class="lib-order-thumbs" aria-hidden="true">
                @for (line of order.lines.slice(0, 3); track line.id) {
                  @if (line.listing_detail?.cover_image) {
                    <img [src]="line.listing_detail.cover_image" alt="" loading="lazy" />
                  } @else {
                    <span class="lib-order-thumb-placeholder"></span>
                  }
                }
              </div>
              <div class="lib-order-titles">
                @for (line of order.lines; track line.id; let last = $last) {
                  <a [routerLink]="['/marketplace/listing', line.listing]">{{ line.listing_detail?.title || 'Listing #' + line.listing }}</a>@if (!last) {<span>, </span>}
                }
              </div>
            </div>
            <span [class]="'lib-status lib-status--' + order.status">
              <span class="lib-status-dot" aria-hidden="true"></span>{{ statusLabel(order.status) }}
            </span>
            <span class="lib-order-total">{{ money(order.total) }}</span>
          </div>
        }
      </div>
    }
  }
</div>
</main>
<app-footer></app-footer>
